import type { Dayjs } from 'dayjs';
import { useMemo } from 'react';

import { getTimezoneDayjs } from '@/utils/time';

import {
  getSelectedPeriodDateRange1x,
  getSignalDatePresetBucketCount,
  parseSignalDatePreset,
} from '../lib/signal-date-preset.utils';
import { changeSort } from '../lib/signal-issue.utils';
import type {
  ChartIssueDateChunk,
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '../model';

export type ChartMappedIssue = CompositedSignalIssue & {
  mappedData: ChartIssueDateChunk[];
};

type UseChartMappedDataOptions = {
  sortStatus?: NotNullableSortStatusT;
  topCount?: number;
};

/**
 * @description
 * 차트 렌더링을 위해 issues에 mappedData를 추가합니다.
 * 선택 기간(1x)의 날짜 맵을 생성하고, 빈 날짜는 0으로 채웁니다.
 * sortStatus와 topCount 옵션이 주어지면 정렬 및 상위 N개만 반환합니다.
 */
export const useChartMappedData = (
  issues: CompositedSignalIssue[],
  datePreset: SignalDateSelectionInput,
  options?: UseChartMappedDataOptions,
): ChartMappedIssue[] => {
  const { manipulateType } = parseSignalDatePreset(datePreset);
  const bucketCount = getSignalDatePresetBucketCount(datePreset);
  const { sortStatus, topCount } = options || {};

  return useMemo(() => {
    // 선택 기간(1x)의 날짜 맵 생성
    const [startDate] = getSelectedPeriodDateRange1x(datePreset);
    const dateMap: Dayjs[] = [];
    for (let i = 0; i < bucketCount; i++) {
      dateMap.push(startDate.addVal(i, manipulateType));
    }

    // sortStatus가 있으면 정렬
    let sortedIssues = [...issues];
    if (sortStatus) {
      const { sortBy, orderStatus } = sortStatus;
      const direction = orderStatus === 'asc' ? 1 : -1;

      sortedIssues.sort((a, b) => {
        switch (sortBy) {
          case 'feedback':
            // asc: least to most, desc: most to least
            return (a.selectedPeriodCount - b.selectedPeriodCount) * direction;
          case 'changes':
            // changeSort returns "most changed first" by default
            // negate to align with direction (asc=least, desc=most)
            return -changeSort(a, b) * direction;
          default:
            return 0;
        }
      });
    }

    const slicedIssues = topCount
      ? sortedIssues.slice(0, topCount)
      : sortedIssues;

    return slicedIssues.map((issue) => {
      const values = issue?.issueDateChunks || [];

      // O(1) 조회를 위해 Map으로 변환 (기존 find()는 O(n) 선형 탐색)
      const valueMap = new Map<number, number>();
      for (const v of values) {
        const key = getTimezoneDayjs(v.date)
          .startOf(manipulateType)
          .valueOf();
        valueMap.set(key, v.value);
      }

      // 날짜 맵에 values 매핑, 빈 날짜는 0으로 채움
      const mappedData = dateMap.map((date) => {
        const normalizedDate = date.startOf(manipulateType);
        const timestamp = normalizedDate.valueOf();
        return {
          date: normalizedDate.toJSON(),
          value: valueMap.get(timestamp) ?? 0,
          timestamp,
        };
      });

      return {
        ...issue,
        mappedData,
      };
    });
  }, [issues, datePreset, manipulateType, bucketCount, sortStatus, topCount]);
};
